<template>
  <form @submit.prevent="submit">
    <suspense>
      <slot />
    </suspense>
  </form>
</template>

<script>
import pick from 'lodash/pick';
import groupBy from 'lodash/groupBy';
import qs from 'qs';
import {
  computed,
  ref,
  provide,
  inject,
  readonly,
  reactive,
  toRefs,
  watch,
} from 'vue';

export const UPDATE_TYPES = {
  attribute: 'attribute',
  class: 'class',
  modifierClass: 'modifierClass',
  initialValue: 'initialValue',
};

export class FieldUpdate {
  constructor({ name, type, value }) {
    if (!Object.values(UPDATE_TYPES).includes(type)) {
      console.error('Invalid Type ', this.type, UPDATE_TYPES);
    }

    this.name = name;
    this.type = type;
    this.value = value;
  }
  toObject() {
    return this;
  }
}

function GetObjectWithoutEmptyValues(object) {
  return Object.fromEntries(
    Object.entries(object)
      .sort(([key]) => key)
      .filter(([key, value]) => value != null && value != '')
  );
}

export default {
  provide() {
    const formContext = {};

    Object.defineProperty(formContext, 'uuid', {
      enumerable: true,
      get: () => this.uuid,
    });

    Object.defineProperty(formContext, 'fields', {
      enumerable: true,
      get: () => this.internalFields,
    });

    Object.defineProperty(formContext, 'dirtyFields', {
      enumerable: true,
      get: () => this.dirtyFields,
    });

    Object.defineProperty(formContext, 'isDirty', {
      enumerable: true,
      get: () => this.isDirty,
    });

    Object.defineProperty(formContext, 'errorFields', {
      enumerable: true,
      get: () => this.errorFields,
    });

    Object.defineProperty(formContext, 'fieldUpdateHistory', {
      enumerable: true,
      get: () => this.fieldUpdateHistory,
    });

    return {
      formContext: formContext,
      getFieldValue: this.getFieldValue,
      storeFieldValue: this.storeFieldValue,
      deleteField: this.deleteField,
      resetForm: this.resetForm,
      getFields: this.getFields,
      getInitialFields: this.getInitialFields,
      submit: this.submit,
      setPreStoredFields: this.setPreStoredFields,

      addFieldUpdate: this.addFieldUpdate,
      deleteFieldUpdate: this.deleteFieldUpdate,
      setFieldUpdate: this.setFieldUpdate,
      getFieldUpdatesByName: this.getFieldUpdatesByName,
    };
  },

  // model: {
  //   prop: 'fields',
  //   event: 'change',
  // },

  props: {
    action: {
      type: String,
      default: '',
    },
    method: {
      type: String,
      default: 'POST', // so ist's bei <form>, aber wir nutzen vlt. lieber post?
    },
    enctype: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '',
    },
    fields: {
      type: Object,
      default() {
        return {};
      },
    },
    beforeSubmitHook: {
      type: Function,
      default() {
        return () => true;
      },
    },
  },

  emits: ['update:fields'],

  setup(props, { emit }) {
    const fields = reactive({
      preStored: {},
      initial: {},
      internal: {},
    });

    const fieldsDirty = computed(() => {
      const fieldsDirty = Object.entries(fields.internal)
        .filter(([key, value]) => {
          return JSON.stringify(fields.initial[key]) !== JSON.stringify(value);
        })
        .map(([key]) => key);
      return fieldsDirty;
    });

    const preStoreFields = preStoreFields => {
      const overwriteFields = pick(
        preStoreFields,
        Object.keys(fields.internal)
      );

      fields.internal = {
        ...fields.internal,
        ...overwriteFields,
      };

      fields.preStored = JSON.parse(JSON.stringify(preStoreFields));
    };

    preStoreFields(props.fields);

    const storeFieldValue = (name, value) => {
      const isFirstTime = fields.internal[name] === undefined;
      if (isFirstTime) {
        fields.initial[name] = value;
      }

      const preStoredValue = fields.preStored[name];

      if (isFirstTime && preStoredValue) {
        fields.internal[name] = preStoredValue;
        return;
      }

      fields.internal[name] = value;
    };

    const getFieldValue = name => {
      return fields.internal[name] || '';
    };

    const deleteField = name => {
      delete fields.internal[name];
      delete fields.initial[name];
    };

    const reset = ({ keepFields }) => {
      if (JSON.stringify(fields.internal) === JSON.stringify(fields.initial)) {
        // form already has inital state
        return;
      }

      const keptFields = Object.fromEntries(
        keepFields.map(name => [name, fields.internal[name]])
      );

      fields.internal = JSON.parse(
        JSON.stringify({ ...fields.initial, ...keptFields })
      );
    };

    const submit = event => {
      console.log({ fields });
      if (!props.beforeSubmitHook(fields.internal)) return;

      if (props.method === 'GET') {
        const fieldsWithValue = GetObjectWithoutEmptyValues(fields.internal);

        const searchParams = qs.stringify(fieldsWithValue, {
          arrayFormat: 'indices',
          encode: false,
        });

        // TODO: find out how to make this work with relative paaths
        // const url = new URL(this.action);
        // url.search = searchParams;

        window.location.href = `${props.action}?${searchParams}`;

        return;
      }

      const form = document.createElement('form');
      form.action = props.action;
      form.method = props.method;
      form.target = props.target;
      form.enctype = props.enctype;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'data';
      input.value = JSON.stringify(fields.internal);

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    };

    watch(
      props.fields,
      fields => {
        fields.internal = fields;
      },
      { deep: true }
    );

    watch(
      fields,
      fields => {
        emit('update:fields', fields.internal);
      },
      { deep: true }
    );

    provide('form/fields', readonly(fields));
    provide('form/storeFieldValue', storeFieldValue);
    provide('form/getFieldValue', getFieldValue);
    provide('form/deleteField', deleteField);
    provide('form/submit', submit);
    provide('form/reset', reset);
    provide('form/preStoreFields', preStoreFields);

    if (props.fields.value && Object.keys(fields.preStored).length === 0) {
      preStore(props.fields.value);
    }

    return {
      internalFields: fields.internal,
      initialFields: fields.initial,
      preStoredFields: fields.preStored,
      dirtyFields: fieldsDirty,
      setPreStoredFields: preStoreFields,
      storeFieldValue,
      getFieldValue,
      deleteField,
      submit,
      reset,
    };
  },

  data() {
    return {
      fieldUpdateHistory: {},
      errorFields: [],
      uuid: this.$uuid.v1(),
    };
  },

  computed: {
    isDirty() {
      return this.dirtyFields.length > 0;
    },
    fieldUpdatesByName() {
      return groupBy(Object.values(this.fieldUpdateHistory), 'name');
    },
  },

  methods: {
    addFieldUpdate({ name, type, value }) {
      if (!Object.values(UPDATE_TYPES).includes(type)) return false;
      const update = new FieldUpdate(name, type, value);
      const updateId = this.$uuid.v1();
      this.fieldUpdateHistory[updateId] = update;
      // this.$set(this.fieldUpdateHistory, updateId, update);
      return updateId;
    },

    setFieldUpdate(updateId, update) {
      this.fieldUpdateHistory[updateId] = update.toObject();
      // this.$set(this.fieldUpdateHistory, updateId, update.toObject());

      return updateId;
    },

    deleteFieldUpdate(updateId) {
      delete this.fieldUpdateHistory[updateId];
      // this.$delete(this.fieldUpdateHistory, updateId);
    },

    getFieldUpdatesByName(name) {
      const fieldUpdates = groupBy(this.fieldUpdatesByName[name], 'type');
      return fieldUpdates;
    },

    getFields() {
      return this.internalFields || {};
    },
    getInitialFields() {
      return this.initialFields || {};
    },

    resetForm({ keepFields }) {
      this.reset({ keepFields });
    },
  },
};
</script>
